import ks from "../ReUsableTS/kBrowserShared";
import { kStyles } from "../ReUsableTS/kStyles";
import LNG from "../ReUsableTS/kLNG";
import { kModalDialog } from "../ReUsableTS/kModalDialog";
import { kEditableTable, kEditableColumn, kHTMLEditableColType } from "../ReUsableTS/kEditableTable";
import { kColumnFilter, kFilterOperator } from "../ReUsableTS/kEditableTableFilter";
import { kColumnFilterPopup } from "../ReUsableTS/kEditableTableFilterPopup";

// One condition row in the advanced dialog: a column picker, an operator picker
// (rebuilt when the column changes), a value box and a remove button. Its own
// object so each control's handler reads `this` — no closures over the loop var.
export class kAdvancedFilterRow {
    public Grid: kEditableTable | null = null;
    public Dialog: kAdvancedFilterDialog | null = null;
    public RowEl: HTMLDivElement | null = null;
    public ColumnSelect: HTMLSelectElement | null = null;
    public OperatorSelect: HTMLSelectElement | null = null;
    public ValueInput: HTMLInputElement | null = null;
    public Removed: boolean = false;

    public Build = (Grid: kEditableTable, Dialog: kAdvancedFilterDialog, Existing: kColumnFilter | null): HTMLDivElement => {
        if (ks.KErrorMode === true) { return document.createElement("div"); }
        try {
            const Row: HTMLDivElement = document.createElement("div");
            this.Grid = Grid;
            this.Dialog = Dialog;
            Row.className = "kAdvFilterRow";
            this.RowEl = Row;
            this._BuildColumnSelect(Row, Existing);
            this._BuildOperatorSelect(Row);
            this._BuildValueInput(Row, Existing);
            this._BuildRemove(Row);
            if (Existing !== null) { this._ApplyExisting(Existing); }
            return Row;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow.Build");
            return document.createElement("div");
        }
    };

    private _BuildColumnSelect = (Row: HTMLDivElement, Existing: kColumnFilter | null): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null) { return; }
            const Select: HTMLSelectElement = document.createElement("select");
            Select.className = "kAdvFilterCol";
            let CurrCol: kEditableColumn;
            for (CurrCol of this.Grid.Columns.Items) {
                if (kAdvancedFilterRow.IsFilterable(CurrCol) === false) { continue; }
                const Option: HTMLOptionElement = document.createElement("option");
                Option.value = CurrCol.KObjectPropertyName;
                Option.textContent = CurrCol.Title;
                Select.appendChild(Option);
            }
            if (Existing !== null) { Select.value = Existing.PropName; }
            Select.addEventListener("change", this._OnColumnChange);
            Row.appendChild(Select);
            this.ColumnSelect = Select;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._BuildColumnSelect");
        }
    };

    private _BuildOperatorSelect = (Row: HTMLDivElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Select: HTMLSelectElement = document.createElement("select");
            Select.className = "kAdvFilterOp";
            Select.addEventListener("change", this._OnOperatorChange);
            Row.appendChild(Select);
            this.OperatorSelect = Select;
            this._PopulateOperators();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._BuildOperatorSelect");
        }
    };

    private _PopulateOperators = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.OperatorSelect === null) { return; }
            this.OperatorSelect.innerHTML = "";
            const Column: kEditableColumn | null = this._CurrentColumn();
            if (Column === null) { return; }
            const Operators: kFilterOperator[] = kColumnFilter.OperatorsFor(Column.Type);
            let CurrOp: kFilterOperator;
            for (CurrOp of Operators) {
                const Option: HTMLOptionElement = document.createElement("option");
                Option.value = CurrOp.toString();
                Option.textContent = kColumnFilterPopup.OperatorLabel(CurrOp);
                this.OperatorSelect.appendChild(Option);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._PopulateOperators");
        }
    };

    private _BuildValueInput = (Row: HTMLDivElement, Existing: kColumnFilter | null): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Input: HTMLInputElement = document.createElement("input");
            Input.className = "kAdvFilterVal";
            Input.placeholder = LNG.FILTERVALUELABEL;
            const Column: kEditableColumn | null = this._CurrentColumn();
            if (Column !== null) { Input.type = kColumnFilterPopup.ValueInputType(Column.Type); }
            if (Existing !== null) { Input.value = Existing.Value; }
            Row.appendChild(Input);
            this.ValueInput = Input;
            this._SyncValueState();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._BuildValueInput");
        }
    };

    private _BuildRemove = (Row: HTMLDivElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Remove: HTMLButtonElement = document.createElement("button");
            Remove.type = "button";
            Remove.className = "kAdvFilterRemove";
            Remove.textContent = "×";
            // "×" alone is meaningless to a screen reader; name the action.
            Remove.setAttribute("aria-label", LNG.FILTERCLEARTHISCOLUMN);
            Remove.title = LNG.FILTERCLEARTHISCOLUMN;
            Remove.addEventListener("click", this._OnRemove);
            Row.appendChild(Remove);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._BuildRemove");
        }
    };

    private _ApplyExisting = (Existing: kColumnFilter): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.OperatorSelect !== null && Existing.Operator !== kFilterOperator.InList) {
                this.OperatorSelect.value = Existing.Operator.toString();
            }
            this._SyncValueState();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._ApplyExisting");
        }
    };

    private _OnColumnChange = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._PopulateOperators();
            const Column: kEditableColumn | null = this._CurrentColumn();
            if (Column !== null && this.ValueInput !== null) {
                this.ValueInput.type = kColumnFilterPopup.ValueInputType(Column.Type);
            }
            this._SyncValueState();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._OnColumnChange");
        }
    };

    private _OnOperatorChange = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._SyncValueState();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._OnOperatorChange");
        }
    };

    private _SyncValueState = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.OperatorSelect === null || this.ValueInput === null) { return; }
            const Operator: kFilterOperator = ks.N0(this.OperatorSelect.value);
            if (kColumnFilterPopup.IsUnaryOperator(Operator) === true) {
                this.ValueInput.style.visibility = "hidden";
            } else {
                this.ValueInput.style.visibility = "visible";
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._SyncValueState");
        }
    };

    private _OnRemove = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Removed = true;
            if (this.RowEl !== null && this.RowEl.parentNode !== null) {
                this.RowEl.parentNode.removeChild(this.RowEl);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._OnRemove");
        }
    };

    private _CurrentColumn = (): kEditableColumn | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            if (this.Grid === null || this.ColumnSelect === null) { return null; }
            let CurrCol: kEditableColumn;
            for (CurrCol of this.Grid.Columns.Items) {
                if (CurrCol.KObjectPropertyName === this.ColumnSelect.value) { return CurrCol; }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow._CurrentColumn");
            return null;
        }
    };

    // The condition this row represents, or null when removed / incomplete.
    public GetFilter = (): kColumnFilter | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            if (this.Removed === true) { return null; }
            const Column: kEditableColumn | null = this._CurrentColumn();
            if (Column === null || this.OperatorSelect === null) { return null; }
            const Filter: kColumnFilter = new kColumnFilter();
            Filter.PropName = Column.KObjectPropertyName;
            Filter.ColType = Column.Type;
            Filter.Title = Column.Title;
            Filter.Operator = ks.N0(this.OperatorSelect.value);
            if (this.ValueInput !== null) { Filter.Value = this.ValueInput.value; }
            if (Filter.IsEmptyCondition() === true) { return null; }
            return Filter;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow.GetFilter");
            return null;
        }
    };

    public static IsFilterable = (Column: kEditableColumn): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Column.Filterable === false) { return false; }
            if (Column.Type === kHTMLEditableColType.Button) { return false; }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterRow.IsFilterable");
            return false;
        }
    };
}

// The Tier-3 advanced cross-column filter dialog. Reuses kModalDialog (focus
// trap, Esc, OK/Cancel). Lets the user build several column conditions that are
// AND-combined, then commits them to the grid's FilterModel on OK. ONE concern:
// edit the whole column-condition set.
export class kAdvancedFilterDialog {
    public Grid: kEditableTable | null = null;
    public Dialog: kModalDialog | null = null;
    public Rows: kAdvancedFilterRow[] = [];
    public RowsHost: HTMLDivElement | null = null;

    public Open = (Grid: kEditableTable): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kAdvancedFilterDialog.AddStyles();
            this.Grid = Grid;
            this.Rows = [];
            const Dialog: kModalDialog = new kModalDialog(LNG.FILTERADVANCED);
            Dialog.DestroyOnClose = true;
            Dialog.Width = 460;
            Dialog.Height = 360;
            Dialog.showOKButton = true;
            Dialog.showCancelButton = true;
            Dialog.OKCB = this._OnOK;
            Dialog.SetGreyTitle();
            Dialog.SetContentElement(this._BuildContent());
            this.Dialog = Dialog;
            Dialog.Show();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterDialog.Open");
        }
    };

    // Closes the modal if it is open (used on grid teardown). kModalDialog.Close
    // is a no-op cleanup when already closed.
    public Close = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Dialog !== null) { this.Dialog.Close(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterDialog.Close");
        }
    };

    private _BuildContent = (): HTMLElement => {
        if (ks.KErrorMode === true) { return document.createElement("div"); }
        try {
            const Wrap: HTMLDivElement = document.createElement("div");
            Wrap.className = "kAdvFilter";
            const Hint: HTMLDivElement = document.createElement("div");
            Hint.className = "kAdvFilterHint";
            Hint.textContent = LNG.FILTERMATCHALL;
            Wrap.appendChild(Hint);
            const Host: HTMLDivElement = document.createElement("div");
            Host.className = "kAdvFilterRows";
            Wrap.appendChild(Host);
            this.RowsHost = Host;
            this._SeedRows();
            const Add: HTMLButtonElement = document.createElement("button");
            Add.type = "button";
            Add.className = "kAdvFilterAdd";
            Add.textContent = "+ " + LNG.FILTERADDCONDITION;
            Add.addEventListener("click", this._OnAdd);
            Wrap.appendChild(Add);
            return Wrap;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterDialog._BuildContent");
            return document.createElement("div");
        }
    };

    private _SeedRows = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null) { return; }
            const Existing: kColumnFilter[] = this.Grid.FilterModel.ColumnFilters;
            let CurrFilter: kColumnFilter;
            for (CurrFilter of Existing) {
                if (CurrFilter.Operator === kFilterOperator.InList) { continue; }
                this._AddRow(CurrFilter);
            }
            if (this.Rows.length === 0) { this._AddRow(null); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterDialog._SeedRows");
        }
    };

    private _OnAdd = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._AddRow(null);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterDialog._OnAdd");
        }
    };

    private _AddRow = (Existing: kColumnFilter | null): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null || this.RowsHost === null) { return; }
            const Row: kAdvancedFilterRow = new kAdvancedFilterRow();
            this.RowsHost.appendChild(Row.Build(this.Grid, this, Existing));
            this.Rows.push(Row);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterDialog._AddRow");
        }
    };

    // Replace the whole column-condition set with what the dialog holds (the
    // global term is untouched), then re-apply.
    private _OnOK = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null) { return; }
            const NewFilters: kColumnFilter[] = [];
            let CurrRow: kAdvancedFilterRow;
            for (CurrRow of this.Rows) {
                const Filter: kColumnFilter | null = CurrRow.GetFilter();
                if (Filter !== null) { NewFilters.push(Filter); }
            }
            this.Grid.FilterModel.ColumnFilters = NewFilters;
            this.Grid.ApplyFilters();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterDialog._OnOK");
        }
    };

    public static AddStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (document.getElementById("kAdvFilterStyles") !== null) { return; }
            const Css: string = `.kAdvFilterHint{color:#605e5c;margin-bottom:10px;}`
                + `.kAdvFilterRow{display:flex;gap:6px;margin-bottom:8px;align-items:center;}`
                + `.kAdvFilterCol,.kAdvFilterOp,.kAdvFilterVal{padding:4px 6px;border:1px solid #c8c6c4;`
                + `border-radius:4px;flex:1;min-width:0;}`
                + `.kAdvFilterRemove{border:none;background:none;cursor:pointer;font-size:18px;color:#605e5c;}`
                + `.kAdvFilterAdd{background:none;border:none;color:#0078d4;cursor:pointer;text-decoration:underline;}`;
            kStyles.AddStyles("kAdvFilterStyles", Css);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAdvancedFilterDialog.AddStyles");
        }
    };
}
